import { defaultIfEmpty, filter, map, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, Observable, of } from 'rxjs';

import { Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { SidebarService } from '@services/sidebar.service';
import { PendoAnalytics } from '@services/pendo-analytics';

import { extractResponseBody } from '@app/lib/utils';
import { ServerApi } from '@app/services/server-api';
import { normaliseMicros } from '@app/admin/fleet-card/shared/utils';

import {
  IFleetCardOffer,
  IFleetCardOffersBanner,
  IFleetCardOffersBannerServerResponse,
  IFleetCardOfferServerResponse,
  IFleetCardOffersOverview,
  IFleetCardOffersOverviewServerResponse,
} from './interfaces';
import { IBonusRebates } from '@services/fleet-cards-data';

import { BONUS_REBATES_OFFER_ID, OFFER_TYPES, PROMO_OFFERS_STATUSES } from './constants';

@Injectable({ providedIn: 'root' })
export class FleetCardsOffersService {
  private readonly BASE_URL: string = 'fleetcard/w1';

  public triggerPageHeaderRefresh$: Subject<void> = new Subject<void>();
  private offersData$: BehaviorSubject<IFleetCardOffersOverview | null> =
    new BehaviorSubject<IFleetCardOffersOverview | null>(null);
  public openDrawer$: Subject<IFleetCardOffer> = new Subject<IFleetCardOffer>();

  constructor(
    private api: ServerApi,
    private sidebarService: SidebarService,
    private pendo: PendoAnalytics,
    private decimalPipe: DecimalPipe,
  ) { }

  private fetchOffersOverview(): Observable<IFleetCardOffersOverview> {
    return this.api.get(`${this.BASE_URL}/promotion_campaigns/overview`).pipe(
      map(extractResponseBody),
      map(this.transformOffersOverviewResponse.bind(this)),
      tap((offers: IFleetCardOffersOverview) => this.offersData$.next(offers)),
    );
  }

  public getOfferDetail(offerId: string): Observable<IFleetCardOffer> {
    return this.api.get(`${this.BASE_URL}/promotion_campaigns/${offerId}`).pipe(
      map(extractResponseBody),
      map(this.transformOffer.bind(this)),
    );
  }

  public openDrawer(offer: IFleetCardOffer, source: 'benefits_page' | 'overview_sidebar' | 'banner' | 'notification'): void {
    this.openDrawer$.next(offer);
    this.pendo.trackEvent(`promo_platform_${offer.id}_drawer_opened`, { source });
  }

  private offersData(): Observable<IFleetCardOffersOverview> {
    const currentData: IFleetCardOffersOverview = this.offersData$.getValue();
    if (currentData) {
      return of(currentData);
    } else {
      return this.fetchOffersOverview();
    }
  }

  private resetOffersData(): void {
    this.offersData$.next(null);
  }

  public getOffersBanner(): Observable<IFleetCardOffersBanner> {
    return this.offersData().pipe(
      map((offersOverview: IFleetCardOffersOverview) => offersOverview.banner),
    );
  }

  public getNewOffersCount(): Observable<number> {
    return this.offersData().pipe(
      map((offersOverview: IFleetCardOffersOverview) => offersOverview.newOfferCount),
    );
  }

  public getOffers(): Observable<IFleetCardOffer[]> {
    return this.offersData().pipe(
      map((offersOverview: IFleetCardOffersOverview) => offersOverview.offers),
      filter((offers: IFleetCardOffer[]) => {
        return offers && offers.length > 0;
      }),
      defaultIfEmpty([]),
    );
  }

  public acknowledgeBanner(offerId: string, action: string): Observable<{}> {
    return this.api.patch(`${this.BASE_URL}/promotion_campaigns/${offerId}/acknowledgements`,
      {
        acknowledgements: {
          [action]: true,
        },
      },
    ).pipe(
      tap(this.resetOffersData.bind(this)),
      tap(this.fetchOffersOverview.bind(this)),
      tap(() => this.hideNotificationDotIfNoNewOffers()),
    );
  }

  public mapBonusRebatesToOffer(bonusRebatesData: IBonusRebates): IFleetCardOffer {
    const formattedPurchaseRequired: string = this.decimalPipe.transform(bonusRebatesData.purchaseRequired, '1.0-0');
    return {
      id: BONUS_REBATES_OFFER_ID,
      name: $localize`Bonus Rebates`,
      offerType: OFFER_TYPES.FUEL_BASED,
      offerViewed: true,
      progressPercent: Math.min((bonusRebatesData.purchaseToDate / bonusRebatesData.threshold) * 100, 100).toString(),
      promotionCycleEndTime: '',
      promotionCycleStartTime: '',
      currentStatus: bonusRebatesData.currentStatus as unknown as PROMO_OFFERS_STATUSES,
      daysLeft: bonusRebatesData.daysLeft,
      enabled: bonusRebatesData.enabled,
      rewardAmount: bonusRebatesData.additionalRebatesPerUnitInCents,
      thresholdAmount: bonusRebatesData.threshold,
      currentSpend: bonusRebatesData.purchaseToDate,
      overviewStatusMessageHTML: { body: $localize`<b>${formattedPurchaseRequired} gal </b> to go before +${bonusRebatesData.additionalRebatesPerUnitInCents}¢/gal off` },
      detailStatusMessageHTML: { body: '' },
      extraAttrs: {
        nextInvoiceDate: '',
        rewardsIssued: false,
      },
    };
  }

  private hideNotificationDotIfNoNewOffers(): void {
    this.getNewOffersCount().subscribe((newOfferCount: number) => {
      this.sidebarService.setFleetCardNotificationDot(true, newOfferCount > 0);
      this.triggerPageHeaderRefresh$.next(null);
    });
  }

  private transformOffersOverviewResponse(response: IFleetCardOffersOverviewServerResponse): IFleetCardOffersOverview {
    return {
      newOfferCount: response.new_offer_count,
      banner: this.transformBanner(response.banner),
      offers: response?.promotion_campaigns?.map(this.transformOffer),
    };
  }

  private transformOffer(offer: IFleetCardOfferServerResponse): IFleetCardOffer {
    return {
      enabled: offer.enabled,
      id: offer.id,
      name: offer.name,
      currentStatus: offer.current_status,
      daysLeft: offer.days_left,
      offerType: offer.offer_type,
      progressPercent: offer.progress_percent,
      promotionCycleEndTime: offer.promotion_cycle_end_time,
      promotionCycleStartTime: offer.promotion_cycle_start_time,
      offerViewed: offer.offer_viewed,
      rewardAmount: normaliseMicros(offer.reward_amount),
      thresholdAmount: normaliseMicros(offer.threshold_amount),
      currentSpend: normaliseMicros(offer.current_spend),
      overviewStatusMessageHTML: offer.overview_status_message_HTML,
      detailStatusMessageHTML: offer.detail_status_message_HTML,
      ...(offer?.benefits_offer_tile && {
        benefitsOfferTile: offer.benefits_offer_tile,
      }),
      ...(offer?.offer_details_drawer && {
        offerDetailsDrawer: {
          detailBodyMessageHTML: offer.offer_details_drawer.detail_body_message_HTML,
          detailStatusMessageHTML: offer.offer_details_drawer.detail_status_message_HTML,
          detailHeaderMessageHTML: offer.offer_details_drawer.detail_header_message_HTML,
        },
      }),
      faqs: offer?.faqs ?? [],
      extraAttrs: {
        nextInvoiceDate: offer.extra_attrs?.next_invoice_date,
        rewardsIssued: offer.extra_attrs?.rewards_issued,
      },
    };
  }

  private transformBanner(banner: IFleetCardOffersBannerServerResponse): IFleetCardOffersBanner {
    return {
      offerId: banner?.promotion_campaign_id,
      status: banner?.status,
      bannerMessageHTML: banner?.banner_message_HTML,
    };
  }

  public shouldShowProgress(offer: IFleetCardOffer): boolean {
    const shouldShowTypes: OFFER_TYPES[] = [OFFER_TYPES.FUEL_BASED, OFFER_TYPES.SPEND_BASED];
    return shouldShowTypes.includes(offer.offerType);
  }
}